import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {getDefaultAssistantId} from "@/actions/settings/assistants/actions";
import {Assistant as ChatAssistant} from "@/components/chat";
import {CHANNEL_TYPE} from "@/enums/ai";
import {useAlert} from "@/hooks/useAlert";
import {useUserProfile} from "@/hooks/useUserProfile";
import {makeWingmanPosthogFailureHook} from "@/lib/wingman-tracing";
import {WingmanClient, resolveWingmanRouting, type WingmanRouting} from "@/services/wingman";
import {signedInWingmanTransport} from "@/services/wingman/bff";
import {Assistant as ApiAssistant} from "@/types/ai";
import {sortAssistants} from "@/utils/ai/sortAssistants";
import {getUserDefaultAssistantId} from "@/utils/ai/userDefaultAssistant";
import { apiLive } from "@/lib/api";
import {GAIL_API} from "@/utils/common/constants/routes";
import {
   GAILGPT_FAVORITES_KEY,
   GAILGPT_USER_DEFAULT_ASSISTANT_KEY,
} from "@/utils/common/constants/userProfile";
import {MINUTE} from "@/utils/dates";
import {MIME_GPT_DEFAULT, getDefaultFileCount, getDefaultFileSize} from "@/utils/fileHelpers";

interface UseAssistantsOptions {
   /** Initial assistant ID to select (e.g., from URL params) */
   initialAssistantId?: string | null;
   /** Whether to automatically fetch assistants on mount */
   autoFetch?: boolean;
   type?: CHANNEL_TYPE;
   /** Whether to fetch all assistants (any status), used by channel selectors. */
   fetchAll?: boolean;
}

interface UseAssistantsReturn {
   // State
   assistants: ChatAssistant[];
   selectedAssistant: ChatAssistant | null;
   isLoadingAssistants: boolean;
   /** True if fetching assistants failed after all retries */
   hasFetchError: boolean;

   // Display helpers
   displayAssistants: ChatAssistant[];
   displaySelectedAssistant: ChatAssistant | null;

   // Actions
   handleAssistantChange: (assistant: ChatAssistant) => void;
   updateAssistantData: (assistantId: string, updates: Partial<ChatAssistant>) => void;
   fetchAssistants: () => Promise<void>;
   getAuthenticatedClient: (routing?: WingmanRouting) => Promise<WingmanClient>;

   // Utils
   loadingAssistant: ChatAssistant;

   // File upload helpers
   getFileUploadConfig: () => {
      enabled: boolean;
      acceptedFileTypes: string;
      maxFileSize: number;
      maxFileCount: number;
      totalMaxFileSize: number;
   };
}

// Constants for retry logic
const MAX_RETRY_ATTEMPTS = 3;
const INITIAL_RETRY_DELAY_MS = 1000;

const gailgptFailureHook = makeWingmanPosthogFailureHook("gailgpt");

/** Helper to sleep for a given number of milliseconds */
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// Helper function to convert MIME types to HTML accept string
const convertMimeTypesToAcceptString = (mimeTypes: string[]): string => {
   const mimeToExtension = {
      "image/jpeg": ".jpg,.jpeg",
      "image/png": ".png",
      "image/webp": ".webp",
      "image/gif": ".gif",
      "image/bmp": ".bmp",
      "application/pdf": ".pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
      "application/vnd.ms-excel": ".xls",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
      "text/plain": ".txt",
      "text/html": ".html,.htm",
      "text/markdown": ".md",
      "text/csv": ".csv",
      "text/xml": ".xml",
      "text/json": ".json",
   };

   const extensions = mimeTypes
      .map((mime) => mimeToExtension[mime as keyof typeof mimeToExtension])
      .filter(Boolean)
      .join(",");

   // If we have image types, add the generic image/* pattern
   const hasImages = mimeTypes.some((mime) => mime.startsWith("image/"));
   if (hasImages) {
      return extensions ? `image/*,${extensions}` : "image/*";
   }

   return extensions || "*/*";
};

export const useAssistants = (options: UseAssistantsOptions = {}): UseAssistantsReturn => {
   const {
      initialAssistantId,
      autoFetch = true,
      type = CHANNEL_TYPE.GPT,
      fetchAll = false,
   } = options;
   const {setAlert} = useAlert();
   const {t} = useTranslation("ask");
   const {userProfile, saveFavorites} = useUserProfile();

   const favoriteIds = userProfile.favorites?.[GAILGPT_FAVORITES_KEY] ?? [];

   const userDefaultAssistantId = getUserDefaultAssistantId(userProfile, type);

   const [assistants, setAssistants] = useState<ChatAssistant[]>([]);
   const [selectedAssistant, setSelectedAssistant] = useState<ChatAssistant | null>(null);
   const [isLoadingAssistants, setIsLoadingAssistants] = useState(true);
   const [hasFetchError, setHasFetchError] = useState(false);
   const [defaultAssistantId, setDefaultAssistantId] = useState<string | null>(null);

   // Track the latest defaultAssistantId to guard against race conditions
   const latestDefaultAssistantIdRef = useRef<string | null>(null);

   // Debounce window for persisting the user's default Gail. Picks within
   // this window collapse to one save — see handleAssistantChange.
   const USER_DEFAULT_SAVE_DEBOUNCE_MS = 750;
   const saveDefaultTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   // True once the user explicitly picks an assistant via handleAssistantChange.
   // Locks out the post-hydration user-default re-select effect so we never
   // override a deliberate choice the user has already made this session.
   const userHasOverriddenSelectionRef = useRef(false);

   const clientTimeoutMs = 10 * MINUTE;

   // Track the latest initialAssistantId to guard against race conditions
   const latestInitialAssistantIdRef = useRef<string | null | undefined>(initialAssistantId);
   useEffect(() => {
      latestInitialAssistantIdRef.current = initialAssistantId;
   }, [initialAssistantId]);

   // Keep the defaultAssistantId ref in sync
   useEffect(() => {
      latestDefaultAssistantIdRef.current = defaultAssistantId;
   }, [defaultAssistantId]);

   // Create loading assistant placeholder
   const getLoadingAssistant = (): ChatAssistant => ({
      id: "loading",
      status: "active",
      name: "Gail",
      specialty: "Default Assistant",
      description: "Please wait while we load your available assistants",
      capabilities: "",
   });

   const loadingAssistant = getLoadingAssistant();

   // Convert API Assistant to Chat Assistant
   const mapApiAssistantToChatAssistant = (apiAssistant: ApiAssistant): ChatAssistant => {
      // Find the channel to get file upload configuration
      const channel = apiAssistant.channels?.find((channel) => channel.type === type);

      return {
         id: apiAssistant.id,
         status: apiAssistant.status,
         name: apiAssistant.persona.name,
         specialty: apiAssistant.name,
         description: apiAssistant.description,
         capabilities: JSON.stringify(apiAssistant.tasks),
         model: channel?.model, // Include the model information
         fileUpload: channel?.fileUpload
            ? {
                 enabled: channel.fileUpload.enabled,
                 allowedMimeTypes: channel.fileUpload.allowedMimeTypes ?? [],
                 maxFileSizeBytes: channel.fileUpload.maxFileSizeBytes ?? getDefaultFileSize(),
                 maxFileCount: channel.fileUpload.maxFileCount ?? getDefaultFileCount(),
                 totalMaxFileSizeBytes:
                    channel.fileUpload.totalMaxFileSizeBytes ?? getDefaultFileSize(),
              }
            : {
                 enabled: false,
                 allowedMimeTypes: [],
                 maxFileSizeBytes: getDefaultFileSize(),
                 maxFileCount: getDefaultFileCount(),
                 totalMaxFileSizeBytes: getDefaultFileSize(),
              },
      };
   };

   // Signed-in Wingman calls go through the same-origin BFF so the JWT
   // is attached server-side. The browser never reads the access token.
   const getAuthenticatedClient = async (
      routing?: WingmanRouting
   ): Promise<WingmanClient> => {
      return new WingmanClient({
         timeoutMs: clientTimeoutMs,
         onRequestFailure: gailgptFailureHook,
         ...signedInWingmanTransport(routing ?? resolveWingmanRouting(false)),
      });
   };

   // Fetch a single assistant by ID from API
   const fetchAssistantById = async (assistantId: string) => {
      setIsLoadingAssistants(true);
      try {
         // Fetch the default assistant ID in parallel (don't block on errors)
         const defaultIdPromise = getDefaultAssistantId()
            .then((res) => res?.defaultAssistantId ?? null)
            .catch(() => null);

         const response = await apiLive<ApiAssistant>(
            `${GAIL_API}/.internal/assistants/${assistantId}`
         );

         // Wait for the default ID to be resolved
         const fetchedDefaultId = await defaultIdPromise;
         if (fetchedDefaultId) {
            setDefaultAssistantId(fetchedDefaultId);
            latestDefaultAssistantIdRef.current = fetchedDefaultId;
         }

         if (response) {
            // Skip archived (deleted) assistants — don't resurrect them into the list.
            // Keep existing assistants intact so the user can still pick a different one.
            // Clear selection to avoid leaving the "loading" placeholder as selectedAssistant.
            if (response.status === "archived") {
               setSelectedAssistant(null);
               return;
            }

            const hasRequestedChannelType = response.channels?.some(
               (channel) => channel.type === type
            );

            if (hasRequestedChannelType) {
               const chatAssistant = mapApiAssistantToChatAssistant(response);
               setAssistants((prev) => {
                  const merged = [chatAssistant, ...prev.filter((a) => a.id !== assistantId)];
                  return sortAssistants(merged, {
                     favoriteIds,
                     defaultAssistantId: fetchedDefaultId ?? undefined,
                     sortGeneralFirst: true,
                     sortAlphabetically: true,
                  });
               });
               setSelectedAssistant(chatAssistant);
            } else {
               // Assistant exists but does not support the requested channel type
               setAssistants([]);
               setSelectedAssistant(null);
            }
         } else {
            setAssistants([]);
            setSelectedAssistant(null);
         }
      } catch (error) {
         console.error("Error fetching assistant:", error);
         setAlert({
            status: "error",
            message: t("failedToLoadAssistants"),
            secondsToClose: 10,
         });
      } finally {
         setIsLoadingAssistants(false);
      }
   };

   // Process and set assistants from API response
   const processAssistantsResponse = (
      chatAssistants: ChatAssistant[],
      fetchedDefaultAssistantId: string | null
   ): void => {
      setAssistants(chatAssistants);

      // Use the latest initialAssistantId value at resolve time to avoid stale closures
      const latestInitialAssistantId = latestInitialAssistantIdRef.current;

      if (latestInitialAssistantId) {
         // If the target assistant is in this list, select it. Otherwise, do not override
         // selection here. A separate by-ID fetch effect will select the correct assistant.
         const targetAssistant = chatAssistants.find(
            (assistant) => assistant.id === latestInitialAssistantId
         );
         if (targetAssistant) {
            setSelectedAssistant(targetAssistant);
         }
      } else {
         // No specific assistant requested; choose a sensible default.
         // Priority: user's personal default > org default > first in
         // already-sorted list. The "first in list" tier inherits the
         // sort done in attemptFetchAssistants (favorites > org-default >
         // general > alphabetical), so a "general" assistant is preferred
         // over arbitrary alphabetical first when no defaults match.

         // 1. User's personal default (GPT channel only — see userDefaultAssistantId)
         if (userDefaultAssistantId) {
            const userDefault = chatAssistants.find(
               (assistant) => assistant.id === userDefaultAssistantId
            );
            if (userDefault) {
               setSelectedAssistant(userDefault);
               return;
            }
         }

         // 2. Org default
         if (fetchedDefaultAssistantId) {
            const defaultAssistant = chatAssistants.find(
               (assistant) => assistant.id === fetchedDefaultAssistantId
            );
            if (defaultAssistant) {
               setSelectedAssistant(defaultAssistant);
               return;
            }
         }

         // 3. First in the already-sorted list. fetchAll callers (channel
         // selectors) keep null to signal "no selection implied".
         setSelectedAssistant(fetchAll ? null : (chatAssistants[0] ?? null));
      }
   };

   type FetchAttemptResult =
      | {status: "success" | "empty"; assistants: ChatAssistant[]; defaultId: string | null}
      | {status: "error"; error: Error};

   const attemptFetchAssistants = async (
      url: string,
      defaultIdPromise: Promise<string | null>,
      attempt: number
   ): Promise<FetchAttemptResult> => {
      try {
         const response = await apiLive<{data: ApiAssistant[]}>(url);

         if (!response?.data) {
            return {status: "error", error: new Error("No response data received")};
         }

         const fetchedDefaultId = await defaultIdPromise;
         if (fetchedDefaultId) {
            setDefaultAssistantId(fetchedDefaultId);
            latestDefaultAssistantIdRef.current = fetchedDefaultId;
         }

         const chatAssistants = sortAssistants(
            response.data
               .filter((assistant) => assistant.channels?.some((channel) => channel.type === type))
               .map(mapApiAssistantToChatAssistant),
            {
               favoriteIds,
               defaultAssistantId: fetchedDefaultId ?? undefined,
               sortGeneralFirst: true,
               sortAlphabetically: true,
            }
         );

         const status = chatAssistants.length > 0 ? "success" : "empty";
         return {status, assistants: chatAssistants, defaultId: fetchedDefaultId};
      } catch (error) {
         console.error(
            `Error fetching assistants (attempt ${attempt + 1}/${MAX_RETRY_ATTEMPTS}):`,
            error
         );
         return {
            status: "error",
            error: error instanceof Error ? error : new Error(String(error)),
         };
      }
   };

   // Fetch assistants from API with retry logic for empty responses
   const fetchAssistants = async () => {
      setIsLoadingAssistants(true);
      setHasFetchError(false);

      const smsChannelQuery = type === CHANNEL_TYPE.SMS ? "&channels=Sms" : "";
      const url = `${GAIL_API}/.internal/assistants?limit=1000${
         fetchAll ? "" : "&status=active"
      }${smsChannelQuery}`;

      const defaultIdPromise = getDefaultAssistantId()
         .then((res) => res?.defaultAssistantId ?? null)
         .catch(() => null);

      let lastError: Error | null = null;

      for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
         const result = await attemptFetchAssistants(url, defaultIdPromise, attempt);

         if (result.status === "success") {
            processAssistantsResponse(result.assistants, result.defaultId);
            setIsLoadingAssistants(false);
            return;
         }

         if (result.status === "empty" && attempt === MAX_RETRY_ATTEMPTS - 1) {
            processAssistantsResponse(result.assistants, result.defaultId);
            setIsLoadingAssistants(false);
            return;
         }

         if (result.status === "error") {
            lastError = result.error;
         }

         if (attempt < MAX_RETRY_ATTEMPTS - 1) {
            await sleep(INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt));
         }
      }

      console.error("All retry attempts failed for fetching assistants:", lastError);
      setHasFetchError(true);
      setAlert({
         status: "error",
         message: t("failedToLoadAssistants"),
         secondsToClose: 10,
      });
      setIsLoadingAssistants(false);
   };

   // Get assistants to display
   const getDisplayAssistants = (): ChatAssistant[] => {
      if (isLoadingAssistants) {
         return [loadingAssistant];
      }
      return assistants;
   };

   // Get selected assistant to display
   const getDisplaySelectedAssistant = (): ChatAssistant | null => {
      if (isLoadingAssistants) {
         return loadingAssistant;
      }
      if (assistants.length === 0) {
         return null;
      }
      // If selectedAssistant is null, it means a specific assistant was requested but not found
      // Don't fall back to the first assistant in this case
      return selectedAssistant;
   };

   const updateAssistantData = (assistantId: string, updates: Partial<ChatAssistant>) => {
      setAssistants((prev) => prev.map((a) => (a.id === assistantId ? {...a, ...updates} : a)));
      setSelectedAssistant((prev) => (prev?.id === assistantId ? {...prev, ...updates} : prev));
   };

   const handleAssistantChange = (assistant: ChatAssistant) => {
      if (assistant.id === "loading") {
         return;
      }
      setSelectedAssistant(assistant);
      userHasOverriddenSelectionRef.current = true;

      // Cancel any pending save before deciding what to do — the latest
      // selection always wins. Without this, picking back to the current
      // default (or switching to a non-GPT channel) would let an earlier,
      // now-stale save fire and overwrite the user's intended default.
      if (saveDefaultTimerRef.current) {
         clearTimeout(saveDefaultTimerRef.current);
         saveDefaultTimerRef.current = null;
      }

      // Persist as the user's personal default for the chat dropdown.
      // Debounced so rapid switches (exploration) collapse to one save.
      if (type !== CHANNEL_TYPE.GPT || assistant.id === userDefaultAssistantId) {
         return;
      }

      saveDefaultTimerRef.current = setTimeout(() => {
         saveDefaultTimerRef.current = null;
         saveFavorites(GAILGPT_USER_DEFAULT_ASSISTANT_KEY, [assistant.id]).catch(() => {
            // Silent — if persistence fails the user can just re-select on next session.
         });
      }, USER_DEFAULT_SAVE_DEBOUNCE_MS);
   };

   // Clear any pending default-save timer when the hook unmounts so we don't
   // leak the timer or fire a save against a torn-down React tree.
   useEffect(() => {
      return () => {
         if (saveDefaultTimerRef.current) {
            clearTimeout(saveDefaultTimerRef.current);
            saveDefaultTimerRef.current = null;
         }
      };
   }, []);

   // Auto-fetch on mount
   useEffect(() => {
      if (autoFetch) {
         setSelectedAssistant(loadingAssistant);
         if (initialAssistantId) {
            fetchAssistantById(initialAssistantId);
         } else {
            fetchAssistants();
         }
      }
   }, []);

   // Re-apply the user's personal default when it becomes available *after*
   // assistants have already loaded. This handles the new-device case: the
   // initial fetch resolves before useUserProfile.fetchResources hydrates the
   // profile from the API, so the first selection lands on org-default. Once
   // the profile arrives we promote the user's saved default — but only if
   // we're not on a deep link and the user hasn't already picked something
   // this session.
   useEffect(() => {
      if (initialAssistantId) return;
      if (userHasOverriddenSelectionRef.current) return;
      if (!userDefaultAssistantId) return;
      if (selectedAssistant?.id === userDefaultAssistantId) return;
      const target = assistants.find((a) => a.id === userDefaultAssistantId);
      if (target) {
         setSelectedAssistant(target);
      }
   }, [userDefaultAssistantId, assistants, initialAssistantId, selectedAssistant?.id]);

   // When initialAssistantId becomes available later, ensure it is loaded/selected
   useEffect(() => {
      if (!initialAssistantId) return;

      const targetAssistant = assistants.find((assistant) => assistant.id === initialAssistantId);

      if (targetAssistant) {
         if (selectedAssistant?.id !== targetAssistant.id) {
            setSelectedAssistant(targetAssistant);
         }
         return;
      }

      // Not found in current list; fetch it explicitly by ID (may be inactive)
      fetchAssistantById(initialAssistantId);
   }, [initialAssistantId]);

   // File upload helpers
   const getFileUploadConfig = (): {
      enabled: boolean;
      acceptedFileTypes: string;
      maxFileSize: number;
      maxFileCount: number;
      totalMaxFileSize: number;
   } => {
      if (selectedAssistant?.fileUpload) {
         const {fileUpload} = selectedAssistant;
         return {
            enabled: fileUpload.enabled,
            acceptedFileTypes: convertMimeTypesToAcceptString(MIME_GPT_DEFAULT),
            maxFileSize: fileUpload.maxFileSizeBytes ?? 0,
            maxFileCount: fileUpload.maxFileCount ?? 0,
            totalMaxFileSize: fileUpload.totalMaxFileSizeBytes ?? 0,
         };
      }
      return {
         enabled: false,
         acceptedFileTypes: "",
         maxFileSize: 0,
         maxFileCount: 0,
         totalMaxFileSize: 0,
      };
   };

   return {
      // State
      assistants,
      selectedAssistant,
      isLoadingAssistants,
      hasFetchError,

      // Display helpers
      displayAssistants: getDisplayAssistants(),
      displaySelectedAssistant: getDisplaySelectedAssistant(),

      // Actions
      handleAssistantChange,
      updateAssistantData,
      fetchAssistants,
      getAuthenticatedClient,

      // Utils
      loadingAssistant,

      // File upload helpers
      getFileUploadConfig,
   };
};
